import type React from 'react'
import { createContext, useCallback, useContext, useEffect } from 'react'
import { useBluetoothCharacteristics, useDeviceStatus } from '~/signals/devices'
import { BluetoothStatus, useBluetooth } from '~/hooks/useBluetooth/useBluetooth'
import { from, of, throwError } from 'rxjs'
import { catchError, retry, delay, tap } from 'rxjs/operators'

export interface DeviceContextType {
    connected: boolean
    ready: boolean
    connect: () => Promise<void>
    disconnect: () => void
    sendMessage: (message: string) => void
    status: BluetoothStatus
}

export const DeviceContext = createContext<DeviceContextType>({
    connect: async () => void 0,
    disconnect: () => void 0,
    ready: false,
    connected: false,
    status: BluetoothStatus.Idle,
    sendMessage: async () => void 0,
})

export interface UseDeviceProps {
    setupCharacteristicsListeners?: (
        characteristics: Map<string, BluetoothRemoteGATTCharacteristic>,
    ) => void
    onReady?: () => void
    onIdle?: () => void
    onDisconnected?: () => void
    onTransferring?: () => void
    onSearching?: () => void
}

export const useDevice = (props: UseDeviceProps = {}) => {
    const {
        onReady,
        onDisconnected,
        onTransferring,
        onSearching,
        onIdle,
        setupCharacteristicsListeners,
    } = props
    const context = useContext(DeviceContext)
    const characteristics = useBluetoothCharacteristics()

    useEffect(() => {
        switch (context.status) {
            case BluetoothStatus.Ready:
                if (onReady) {
                    onReady()
                }
                break
            case BluetoothStatus.Searching:
                if (onSearching) {
                    onSearching()
                }
                break
            case BluetoothStatus.Transferring:
                if (onTransferring) {
                    onTransferring()
                }
                break
            case BluetoothStatus.Disconnected:
                if (onDisconnected) {
                    onDisconnected()
                }
                break
            default:
                if (onIdle) {
                    onIdle()
                }
                break
        }
    }, [context.status, onDisconnected, onIdle, onReady, onSearching, onTransferring])

    useEffect(() => {
        if (characteristics) {
            setupCharacteristicsListeners?.(characteristics)
        }
    }, [characteristics, setupCharacteristicsListeners])

    if (!context) {
        throw new Error('useDevice must be used within a DeviceProvider')
    }
    return context
}

export const DeviceProvider = ({ children }: { children: React.ReactNode }) => {
    const { connect, connected, disconnect, ready } = useBluetooth()
    const characteristics = useBluetoothCharacteristics()
    const status = useDeviceStatus()

    const sendMessage = useCallback(
        async (message: string) => {
            const rxChar = characteristics?.get('rx')
            if (connected && rxChar) {
                console.info(`Sending: ${message}`)
                const messageBytes = new Uint8Array(message.length)
                for (let i = 0; i < message.length; i++) {
                    messageBytes[i] = message.charCodeAt(i)
                }
                from(rxChar.writeValue(messageBytes))
                    .pipe(
                        catchError(error => {
                            console.info('Error sending message', error)
                            return throwError(() => new Error('Failed to send message'))
                        }),
                        retry({
                            count: 1,
                            delay: error =>
                                of(error).pipe(
                                    tap(() => console.info('Attempting to send message again...')),
                                    delay(60), // Delay for 60 seconds before retrying
                                ),
                        }),
                    )
                    .subscribe({
                        next: () => console.info('Message sent successfully'),
                        error: error => console.error('Failed after retries', error),
                    })
            } else {
                console.error('Attempted to send message, but no device is connected.')
            }
        },
        [characteristics, connected],
    )

    return (
        <DeviceContext.Provider
            value={{ connect, disconnect, sendMessage, connected, status, ready }}
        >
            {children}
        </DeviceContext.Provider>
    )
}
